import { useState, useEffect, useCallback, useContext } from 'react';
import ReactDOM from 'react-dom';
import ScoringModal from './ScoringModal';
import SelectionModal from './SelectionModal';

import modalStyles from './Modals.module.css';
import styles from './ValidatorAccessModal.module.css';
import { SwapMainAppContext } from '../../../../context/SwapMainAppContext';
import UpgradeMembershipModal from '../../../Data/Fleet/UpgradeMembershipModal';

// ── SVG avatars ──────────────────────────────────────────────────────────────

const ExplorerAvatar = () => (
    <svg viewBox="0 0 40 40" fill="none">
        <circle cx="20" cy="20" r="20" fill="#1a3a2a" />
        <ellipse cx="20" cy="26" rx="9" ry="6" fill="#60c87a" opacity="0.7" />
        <circle cx="20" cy="17" r="6" fill="#60c87a" />
        <circle cx="18" cy="16" r="1.2" fill="#1a3a2a" />
        <circle cx="22" cy="16" r="1.2" fill="#1a3a2a" />
        <path d="M18 19.5 Q20 21 22 19.5" stroke="#1a3a2a" strokeWidth="1" strokeLinecap="round" fill="none" />
    </svg>
);

const AmplifierAvatar = () => (
    <svg viewBox="0 0 40 40" fill="none">
        <circle cx="20" cy="20" r="20" fill="#3a2a10" />
        <ellipse cx="20" cy="26" rx="9" ry="6" fill="#f0a04b" opacity="0.7" />
        <circle cx="20" cy="17" r="6" fill="#f0a04b" />
        <circle cx="18" cy="16" r="1.2" fill="#3a2a10" />
        <circle cx="22" cy="16" r="1.2" fill="#3a2a10" />
        <path d="M18 19.5 Q20 21 22 19.5" stroke="#3a2a10" strokeWidth="1" strokeLinecap="round" fill="none" />
    </svg>
);

const InnovatorAvatar = () => (
    <svg viewBox="0 0 40 40" fill="none">
        <circle cx="20" cy="20" r="20" fill="#2a1a3a" />
        <ellipse cx="20" cy="26" rx="9" ry="6" fill="#c98af0" opacity="0.7" />
        <circle cx="20" cy="17" r="6" fill="#c98af0" />
        <circle cx="18" cy="16" r="1.2" fill="#2a1a3a" />
        <circle cx="22" cy="16" r="1.2" fill="#2a1a3a" />
        <path d="M18 19.5 Q20 21 22 19.5" stroke="#2a1a3a" strokeWidth="1" strokeLinecap="round" fill="none" />
    </svg>
);

// ── Progress pips ────────────────────────────────────────────────────────────

function ProgressPips({ total, done, variant }) {
    return (
        <div className={styles.pips}>
            {Array.from({ length: total }).map((_, i) => {
                const filled = i < done;
                const pipClass = filled
                    ? variant === 'amber'
                        ? styles.pipAmber
                        : styles.pipPurple
                    : '';
                return <div key={i} className={`${styles.pip} ${pipClass}`} />;
            })}
        </div>
    );
}



// ── Tab: Explorer ──────────────────────────────────────────────────────────

function ExplorerTab({ isDisabled }) {
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);

    return (
        <>
        <div className={modalStyles.panelBody}>
            <div className={`${styles.accessCard} ${styles.explorerCard}`}>
                <div className={`${styles.acIcon} ${styles.aciLocked}`}>
                    <svg viewBox="0 0 17 17" fill="none">
                        <rect x="3.5" y="7" width="10" height="7.5" rx="2" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2" />
                        <path d="M5.5 7V5a3 3 0 0 1 6 0v2" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2" strokeLinecap="round" />
                        <circle cx="8.5" cy="10.5" r="1.2" fill="rgba(255,255,255,0.35)" />
                    </svg>
                </div>
                <div className={styles.acBody}>
                    <div className={styles.acTitle}>Review &amp; Earn is not available for Explorer members</div>
                    <div className={styles.acDesc}>
                        Reserved for <span className={styles.hlAmber}>Amplifier</span> and{' '}
                        <span className={styles.hlPurple}>Innovator</span> tier members. Upgrade to participate and start earning Prisma points.
                    </div>
                    <div className={styles.acAction}>
                        <button disabled={isDisabled} className={`${modalStyles.mbtn} ${modalStyles.mbtnCta}`} onClick={() => setShowUpgradeModal(true)}>
                            Upgrade to Amplifier
                        </button>
                    </div>
                </div>
            </div>
        </div>
        {showUpgradeModal && (
            <UpgradeMembershipModal onClose={() => setShowUpgradeModal(false)} onGoBack={() => setShowUpgradeModal(false)} />
        )}
        </>
    );
}

// ── Tab: Amplifier ─────────────────────────────────────────────────────────

function AmplifierTab({
    spotsRemaining,
    spotsTotal = 100,
    reviewsDone,
    reviewsAllowed = 10,
    onSubmit,
    isSubmitting,
    onViewRules,
    isDisabled
}) {
    return (
        <div className={modalStyles.panelBody}>
            <div className={styles.badge}>
                <span className={`${styles.blinkingDot} ${styles.blinkingDotAmber}`} />
                {spotsRemaining ?? '--'} / {spotsTotal} validator spots remaining
            </div>
            <div className={`${styles.accessCard} ${styles.amplifierCard}`}>
                <div className={`${styles.acIcon} ${styles.aciAmplifier}`}>
                    <svg viewBox="0 0 17 17" fill="none">
                        <path d="M3 9l4 4 7-7" stroke="#f0a04b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
                <div className={styles.acBody}>
                    <div className={styles.acTitle}>You can apply for Validator status</div>
                    <div className={styles.acDesc}>
                        Complete <span className={styles.hlAmber}>{reviewsAllowed} reviews</span> this month.
                        Rankings are calculated at month-end — top scorers fill open spots.
                    </div>
                    <div className={styles.progressTrack}>
                        <ProgressPips total={reviewsAllowed} done={reviewsDone} variant="amber" />
                        <span className={styles.pipLabel}>{reviewsDone ?? '--'} / {reviewsAllowed} done</span>
                    </div>
                    <div className={styles.acAction}>
                        <button
                            className={`${modalStyles.mbtn} ${modalStyles.mbtnCta}`}
                            onClick={onSubmit}
                            disabled={isDisabled || isSubmitting}
                        >
                            {isSubmitting ? "Submitting..." : "Start reviewing" }
                        </button>
                        <button className={`${modalStyles.mbtn} ${modalStyles.mbtnGhost}`} onClick={onViewRules}>
                            View scoring rules ↗
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Tab: Innovator ─────────────────────────────────────────────────────────

function InnovatorTab({
    spotsRemaining,
    spotsTotal = 100,
    reviewsDone,
    reviewsAllowed = 30,
    onSubmit,
    isSubmitting,
    onViewRules,
    isDisabled
}) {
    return (
        <div className={modalStyles.panelBody}>
            <div className={styles.badge}>
                <span className={`${styles.blinkingDot} ${styles.blinkingDotPurple}`} />
                {spotsRemaining ?? '--'} / {spotsTotal} priority spots remaining
            </div>
            <div className={`${styles.accessCard} ${styles.innovatorCard}`}>
                <div className={`${styles.acIcon} ${styles.aciInnovator}`}>
                    <svg viewBox="0 0 17 17" fill="none">
                        <path
                            d="M8.5 2l1.65 3.35L14 6.1l-2.75 2.68.65 3.77L8.5 10.75 5.1 12.55l.65-3.77L3 6.1l3.85-.75L8.5 2z"
                            stroke="#c98af0"
                            strokeWidth="1.2"
                            strokeLinejoin="round"
                            fill="rgba(201,138,240,0.12)"
                        />
                    </svg>
                </div>
                <div className={styles.acBody}>
                    <div className={styles.acTitle}>Priority validator queue unlocked</div>
                    <div className={styles.acDesc}>
                        As an Innovator, you have{' '}
                        <span className={styles.hlPurple}>{reviewsAllowed} priority reviews</span> this month.
                        Rankings are calculated at month-end — high-accuracy scores move to the top of the queue.
                    </div>
                    <div className={styles.progressTrack}>
                        <ProgressPips total={reviewsAllowed} done={reviewsDone} variant="purple" />
                        <span className={styles.pipLabel}>{reviewsDone ?? '--'} / {reviewsAllowed} done</span>
                    </div>
                    <div className={styles.acAction}>
                        <button
                            className={`${modalStyles.mbtn} ${modalStyles.mbtnCta}`}
                            onClick={onSubmit}
                            disabled={isDisabled || isSubmitting}
                        >
                            {isSubmitting ? "Submitting..." : "Begin validation" }
                        </button>
                        <button className={`${modalStyles.mbtn} ${modalStyles.mbtnGhost}`} onClick={onViewRules}>
                            Review ranking criteria ↗
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}


// ── Main component ───────────────────────────────────────────────────────────


export default function ValidatorAccessModal({ validatorAccessData : data, onClose, onSubmitReview, isSubmittingReview }) {
    const { userClass } = useContext(SwapMainAppContext);
    const [activeTab, setActiveTab] = useState(userClass);
    const [dontShowAgain, setDontShowAgain] = useState(true);
    const [showScoringModal, setShowScoringModal] = useState(false);
    const [showSelectionModal, setShowSelectionModal] = useState(false);

    const handleClose = useCallback(() => {
        if (onClose) onClose();
    }, [onClose]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') handleClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [handleClose]);

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) handleClose();
    };

    const checkboxColorClass =
        userClass === 'Amplifier Member'
            ? styles.dsaBoxCheckedAmber
            : userClass === 'Innovator Member'
            ? styles.dsaBoxCheckedPurple
            : '';

    const modal = (
        <div className={modalStyles.overlay} onClick={handleOverlayClick}>
            <div className={modalStyles.shell} role="dialog" aria-modal="true" aria-labelledby="va-title">
                {/* Header */}
                <div className={modalStyles.head}>
                    <div className={modalStyles.headInner}>
                        <div className={modalStyles.headLeft}>
                            <span className={modalStyles.eyebrow}>Review &amp; Earn</span>
                            <span className={modalStyles.titleTxt} id="va-title">Validator access</span>
                        </div>
                    </div>
                    <button className={modalStyles.closeBtn} onClick={handleClose} aria-label="Close">
                        ×
                    </button>
                </div>

                {/* Access view */}
                
                    <>
                        {/* Tier tabs */}
                        <div className={styles.tierTabs}>
                            {[
                                { key: 'Explorer Member', label: 'Explorer', Avatar: ExplorerAvatar, tabClass: styles.tierTabExplorer },
                                { key: 'Amplifier Member', label: 'Amplifier', Avatar: AmplifierAvatar, tabClass: styles.tierTabAmplifier },
                                { key: 'Innovator Member', label: 'Innovator', Avatar: InnovatorAvatar, tabClass: styles.tierTabInnovator },
                            ].map(({ key, label, Avatar, tabClass }) => {
                                const isActiveTab = activeTab === key;
                                
                                return (
                                    <button
                                        key={key}
                                        className={`${styles.tierTab} ${tabClass} ${isActiveTab ? styles.active : styles.inactive}`}
                                        onClick={() => setActiveTab(key)}
                                    >
                                        <div className={styles.tabAvatar}>
                                            <Avatar />
                                        </div>
                                        {label}
                                        {userClass === key && (
                                            <span className={styles.youBadge}>You</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Active panel */}
                        {activeTab === 'Explorer Member' && (
                            <ExplorerTab
                                isDisabled={userClass !== 'Explorer Member'}
                            />
                        )}
                        {activeTab === 'Amplifier Member' && (
                            <AmplifierTab
                                spotsRemaining={data?.spots_remaining}
                                spotsTotal={data?.spots_total}
                                reviewsDone={userClass === 'Amplifier Member' ? data?.reviews_done : null}
                                reviewsAllowed={data?.reviews_allowed?.amplifier}
                                isDisabled={userClass !== 'Amplifier Member'}
                                onSubmit={() => onSubmitReview(dontShowAgain)}
                                isSubmitting={isSubmittingReview}
                                onViewRules={() => setShowScoringModal(true)}
                            />
                        )}
                        {activeTab === 'Innovator Member' && (
                            <InnovatorTab
                                spotsRemaining={data?.spots_remaining}
                                spotsTotal={data?.spots_total}
                                reviewsDone={userClass === 'Innovator Member' ? data?.reviews_done : null}
                                isDisabled={userClass !== 'Innovator Member'}
                                reviewsAllowed={data?.reviews_allowed?.innovator}
                                onSubmit={() => onSubmitReview(dontShowAgain)}
                                isSubmitting={isSubmittingReview}
                                onViewRules={() => setShowScoringModal(true)}
                            />
                        )}

                        {/* Footer */}
                        <div className={styles.footer}>
                            <div className={styles.footerLeft}>
                                <label className={styles.dsaWrap}>
                                    <div
                                        className={`${styles.dsaBox} ${dontShowAgain ? checkboxColorClass : ''}`}
                                        onClick={() => setDontShowAgain((v) => !v)}
                                    >
                                        {dontShowAgain && (
                                            <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                                                <path d="M1 3.5l2.5 2.5L8 1" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                    </div>
                                    <span className={styles.dsaLbl}>Don&apos;t show this again</span>
                                </label>
                            </div>
                            <button className={styles.howLinkBtn} onClick={() => setShowSelectionModal(true)} >
                                How selection works ↗
                            </button>
                        </div>
                    </>
                

            </div>
        </div>
    );

    return (
        <>
            {ReactDOM.createPortal(modal, document.body)}
            {showScoringModal && 
                <ScoringModal onClose={() => setShowScoringModal(false)} showBackButton />
            }
            {showSelectionModal &&
                <SelectionModal onClose={() => setShowSelectionModal(false)} showBackButton />
            }
        </>
    );
}